import { ResponsiveBar } from "@nivo/bar";
import { DashboardLayout } from "components/DashboardLayout";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ToolTip from "components/design/toolTip";
import { Header } from "components/DesignSystem/Header/Header";
import { colors } from "constants/colors";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import {
  RevenueSchedule,
  useGetRevenueScheduleQuery,
} from "store/apis/revenueSchedule";
import { patternLinesDef } from "@nivo/core";
import Card from "components/DesignSystem/Card/Card";
import { Info } from "components/icons/Info";
import { Field, FieldProps, Formik } from "formik";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import dayjs from "dayjs";
import { YYYY_MM_DD } from "constants/date";
import { formatTimeToMonthYear } from "utils/formatDate";
import { Button } from "components/DesignSystem/Button/Button";
import Tab from "components/DesignSystem/Tab/Tab";
import { Route, Switch, useRouteMatch } from "react-router-dom";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { LongArrowRight } from "components/icons/LongArrowRight";
import { currency } from "utils/Currency";
import { RevenueWaterfall } from "./RevenueWaterfall";
import { RecognitionPartyWaterfall } from "components/RecognitionPartyWaterfall/RecognitionPartyWaterfall";
import { usePageTitle } from "hooks/usePageTitle";
import Loader from "components/DesignSystem/Loader/loader";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { RevenueRecognitionLocked } from "components/RevenueRecognitionLocked/RevenueRecognitionLocked";
import { useFilters } from "hooks/useFilter";

const RevenueScheduleChart = ({
  schedule,
}: {
  schedule: RevenueSchedule[];
}) => {
  const chartData = schedule
    ?.filter(({ period }) => period !== "Prior Months")
    .map((item) => ({
      period: item.period,
      recognized_revenue: item.recognized_revenue.total_recognized,
      deferred_revenue: item.deferred_revenue.total_deferred,
      period_start: item.period_start,
      period_end: item.period_end,
    }));

  function generateTicks(min: number, max: number, count: number) {
    const step = (max - min) / (count - 1);
    return Array.from({ length: count }, (_, i) => Math.round(min + i * step));
  }

  const minY = 0;
  const maxY = Math.max(
    ...chartData.map(
      (item) => Math.max(item.recognized_revenue, item.deferred_revenue) + 50
    )
  );

  const tickValues = generateTicks(minY, maxY, 3);
  const markers = tickValues.map((value) => ({
    axis: "y" as const,
    value,
    lineStyle: { stroke: colors.neutral[0], strokeWidth: 1, zIndex: 1 },
    legendOrientation: "vertical" as const,
  }));

  return (
    <ResponsiveBar
      data={chartData}
      keys={["recognized_revenue", "deferred_revenue"]}
      groupMode="grouped"
      indexBy="period"
      tooltip={(values) => {
        return (
          <div className="t-bg-neutral-100 t-text-surface t-p-3 t-rounded t-text-body-sm t-break-words t-hyphen-auto t-shadow-dark-30 t-text-left t-flex t-flex-col t-gap-1.5 t-w-44">
            <span>{values.data.period}</span>

            <div className="t-flex t-gap-1">
              <span className="t-rounded-full t-w-0.5 t-h-full  t-bg-purple-40">
                &nbsp;
              </span>
              <span>Recognised</span>
              <span className="t-ml-auto">
                <AmountSuperScript
                  amount={Number(values.data.recognized_revenue || 0)}
                />
              </span>
            </div>
            <div className="t-flex t-gap-1">
              <span className="t-rounded-full t-w-0.5 t-h-full t-bg-neutral-20">
                &nbsp;
              </span>
              <span>Deferred</span>
              <span className="t-ml-auto">
                <AmountSuperScript
                  amount={Number(values.data.deferred_revenue || 0)}
                />
              </span>
            </div>
          </div>
        );
      }}
      markers={markers}
      enableGridY={false}
      axisLeft={{
        tickValues: tickValues,
        format: (value) => {
          return currency({ amount: value });
        },
      }}
      theme={{
        axis: {
          ticks: {
            line: {
              stroke: colors.neutral[0],
              strokeWidth: 1,
            },
            text: {
              fill: colors.neutral[90],
              fontSize: 12,
            },
          },
        },
      }}
      margin={{ top: 30, right: 30, bottom: 50, left: 90 }}
      innerPadding={2}
      padding={0.4}
      enableLabel={false}
      borderRadius={2}
      layers={["markers", "axes", "totals", "bars", "legends", "annotations"]}
      defs={[
        patternLinesDef("deferred_revenue-pattern", {
          spacing: 5,
          rotation: -45,
          lineWidth: 2,
          background: colors.neutral[20],
          color: colors.neutral[40],
        }),

        patternLinesDef("recognized_revenue-pattern", {
          spacing: 5,
          rotation: -45,
          lineWidth: 2,
          background: colors.purple[40],
          color: colors.purple[50],
        }),
      ]}
      fill={[
        {
          match: ({
            data: {
              data: { period, period_start },
              id,
            },
          }) =>
            dayjs(period_start).isSame(dayjs(), "month") &&
            id === "recognized_revenue",
          id: "recognized_revenue-pattern",
        },
        {
          match: ({
            data: {
              data: { period_start },
              id,
            },
          }) =>
            dayjs(period_start).isSame(dayjs(), "month") &&
            id === "deferred_revenue",
          id: "deferred_revenue-pattern",
        },
      ]}
      colors={({ id }) => {
        if (id === "recognized_revenue") return colors.purple[40];
        if (id === "deferred_revenue") return colors.neutral[20];
        return "#000";
      }}
      axisBottom={{
        tickValues: chartData
          .filter((_, index) => index === 0 || index === chartData.length - 1)
          .map((item) => item.period),
      }}
    />
  );
};

const RevenueScheduleOverview = () => {
  const entityId = useCurrentEntityId();

  const {
    values: { startDate, endDate, summaryDateStart },
    updateFilter,
  } = useFilters({
    initialValue: {
      startDate: dayjs().startOf("year").format(YYYY_MM_DD),
      endDate: dayjs().endOf("month").format(YYYY_MM_DD),
      summaryDateStart: dayjs()
        .startOf("year")
        .startOf("month")
        .format(YYYY_MM_DD),
    },
  });

  const {
    data: revenueScheduleDataWithoutPrior,
    isLoading: isLoadingRevenueScheduleWithoutPrior,
  } = useGetRevenueScheduleQuery(
    {
      entityId: entityId,
      start_date: startDate,
      end_date: endDate,
      carry_forward_invoices: false,
    },
    { skip: !entityId }
  );

  const { data: revenueScheduleData, isLoading: isLoadingRevenueSchedule } =
    useGetRevenueScheduleQuery(
      {
        entityId: entityId,
        start_date: startDate,
        end_date: endDate,
        carry_forward_invoices: true,
      },
      { skip: !entityId }
    );

  const { data: summaryData } = useGetRevenueScheduleQuery(
    {
      entityId: entityId,
      start_date: summaryDateStart,
      end_date: dayjs(summaryDateStart).endOf("month").format(YYYY_MM_DD),
      carry_forward_invoices: true,
    },
    { skip: !entityId }
  );
  const summarySchedule = summaryData?.schedule.at(-1);

  if (isLoadingRevenueSchedule || isLoadingRevenueScheduleWithoutPrior) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  return (
    <div className="t-pb-24">
      <Card.Root className="sm:!t-px-3">
        <Card.Header>
          <div className="t-flex t-justify-between t-items-center t-gap-4 t-w-full sm:t-flex-col sm:t-items-start sm:t-gap-2">
            <p className="t-m-0 t-text-subtitle-lg">Revenue Overview</p>
            <div>
              <Formik
                onSubmit={() => {}}
                initialValues={{ startDate: "", endDate: "" }}
              >
                <div className="t-flex t-gap-2 t-items-center">
                  <Field name="startDate">
                    {({ field }: FieldProps) => (
                      <DateInput
                        {...field}
                        showMonthYearPicker
                        label="Start Date"
                        name="startDate"
                        value={startDate}
                        placeholder="Select start date"
                        minDate={dayjs().startOf("year").toDate()}
                        // maxDate={dayjs().endOf("month").toDate()}
                        customDatePickerInputTrigger={
                          <div>
                            <Button size="small">
                              {formatTimeToMonthYear(startDate)}
                            </Button>
                          </div>
                        }
                        onDateChange={(date) =>
                          updateFilter(
                            "startDate",
                            dayjs(date).startOf("month").format(YYYY_MM_DD)
                          )
                        }
                      />
                    )}
                  </Field>

                  <div className="t-text-text-30">
                    <LongArrowRight />
                  </div>

                  <Field name="endDate">
                    {({ field }: FieldProps) => (
                      <DateInput
                        {...field}
                        value={endDate}
                        showMonthYearPicker
                        popperPlacement="bottom-end"
                        label="End Date"
                        customDatePickerInputTrigger={
                          <div>
                            <Button size="small">
                              {formatTimeToMonthYear(endDate)}
                            </Button>
                          </div>
                        }
                        minDate={dayjs(startDate).toDate()}
                        // maxDate={dayjs().endOf("month").toDate()}
                        name="endDate"
                        placeholder="Select end date"
                        onDateChange={(date) =>
                          updateFilter(
                            "endDate",
                            dayjs(date).endOf("month").format(YYYY_MM_DD)
                          )
                        }
                      />
                    )}
                  </Field>
                </div>
              </Formik>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="t-flex t-my-4 sm:t-flex-col">
          <div className="t-flex t-flex-col t-gap-6 t-flex-shrink-0 t-w-[150px] t-mr-6 sm:t-w-full sm:t-mr-0 sm:t-flex-row sm:t-justify-between sm:t-gap-3 sm:t-pb-4">
            <div className="t-flex t-flex-col t-gap-1">
              <span className="t-whitespace-nowrap t-flex t-items-center t-gap-1">
                <span className="t-text-body-sm t-text-text-30">Booked</span>
                <ToolTip
                  text={`Revenue booked from ${formatTimeToMonthYear(
                    startDate
                  )} to ${formatTimeToMonthYear(endDate)}`}
                >
                  <span className="t-text-text-30 t-flex">
                    <Info size="14" stroke="1.4" />
                  </span>
                </ToolTip>
              </span>
              <span className="t-text-subtext t-text-text-60">
                <AmountSuperScript
                  amount={Number(
                    revenueScheduleDataWithoutPrior?.total_booked_revenue || 0
                  )}
                />
              </span>
            </div>

            <div className="t-flex t-flex-col t-gap-1">
              <span className="t-whitespace-nowrap t-flex t-items-center t-gap-1">
                <span className="t-text-body-sm t-text-text-30 t-flex t-items-center t-gap-1">
                  <div className="t-w-1.5 t-h-1.5 t-bg-purple-40 t-rounded-full" />
                  <span className="t-text-body-sm t-text-text-30">
                    Recognised
                  </span>
                </span>
                <ToolTip
                  text={`Revenue recognised from ${formatTimeToMonthYear(
                    startDate
                  )} to ${formatTimeToMonthYear(endDate)}`}
                >
                  <span className="t-text-text-30 t-flex">
                    <Info size="14" stroke="1.4" />
                  </span>
                </ToolTip>
              </span>
              <span className="t-text-subtext t-text-text-60">
                <AmountSuperScript
                  amount={Number(
                    revenueScheduleData?.total_recognized_revenue || 0
                  )}
                />
              </span>
            </div>

            <div className="t-flex t-flex-col t-gap-1">
              <span className="t-whitespace-nowrap t-flex t-items-center t-gap-1">
                <span className="t-text-body-sm t-text-text-30 t-flex t-items-center t-gap-1">
                  <div className="t-w-1.5 t-h-1.5 t-bg-neutral-40 t-rounded-full" />
                  <span>Deferred</span>
                </span>
                <ToolTip
                  text={`Revenue deferred as of ${formatTimeToMonthYear(
                    endDate
                  )}`}
                >
                  <span className="t-text-text-30 t-flex">
                    <Info size="14" stroke="1.4" />
                  </span>
                </ToolTip>
              </span>
              <span className="t-text-subtext t-text-text-60">
                <AmountSuperScript
                  amount={Number(
                    revenueScheduleData?.total_deferred_revenue_as_of_end_date ||
                      0
                  )}
                />
              </span>
            </div>
          </div>
          <div className="t-border-solid t-border-neutral-0 t-border-l t-border-0 sm:t-hidden" />
          <div className="t-h-[300px] t-w-full t-flex-shrink">
            {revenueScheduleData && (
              <RevenueScheduleChart schedule={revenueScheduleData?.schedule} />
            )}
          </div>
        </Card.Body>
      </Card.Root>

      {summarySchedule && (
        <Card.Root className="t-mt-6 sm:!t-px-3">
          <Card.Header>
            <div className="t-flex t-justify-between t-items-center t-gap-4 t-w-full sm:t-flex-col sm:t-items-start sm:t-gap-2">
              <p className="t-m-0 t-text-subtitle-lg">
                {summarySchedule?.period} Summary
              </p>
              <div className="t-text-body-sm t-text-text-60">
                <Formik
                  onSubmit={() => {}}
                  initialValues={{ startDate: "", endDate: "" }}
                >
                  <div className="t-flex t-gap-2 t-items-center">
                    <Field name="startDate">
                      {({ field }: FieldProps) => (
                        <DateInput
                          {...field}
                          showMonthYearPicker
                          value={summaryDateStart}
                          label="Start Date"
                          name="startDate"
                          popperPlacement="bottom-end"
                          placeholder="Select start date"
                          minDate={dayjs().startOf("year").toDate()}
                          // maxDate={dayjs().endOf("month").toDate()}
                          customDatePickerInputTrigger={
                            <div>
                              <Button size="small">
                                {formatTimeToMonthYear(summaryDateStart)}
                              </Button>
                            </div>
                          }
                          onDateChange={(date) => {
                            updateFilter(
                              "summaryDateStart",
                              dayjs(date).startOf("month").format(YYYY_MM_DD)
                            );
                          }}
                        />
                      )}
                    </Field>
                  </div>
                </Formik>
              </div>
            </div>
          </Card.Header>
          <Card.Body className="t-mb-4">
            <div className="t-space-y-6">
              {/* Booked Revenue */}
              <div>
                <h3 className="t-text-subtext t-text-text-100 t-mb-3">
                  Booked Revenue
                </h3>
                <div className="t-flex t-justify-between t-items-center sm:t-gap-3 t-py-2 t-border-t t-border-neutral-10 t-text-body-sm t-text-text-60 t-bg-neutral-0 t-px-3 t-rounded">
                  <span className="t-text-body-sm t-text-text-60">
                    Revenue booked in {summarySchedule?.period}
                  </span>
                  <span className="t-text-body-sm t-text-text-60">
                    <AmountSuperScript
                      amount={Number(
                        summarySchedule?.booked_revenue
                          .booked_from_this_period_sum || 0
                      )}
                    />
                  </span>
                </div>
              </div>

              {/* Recognised Revenue */}
              <div>
                <h3 className="t-text-subtext t-text-text-100 t-mb-3">
                  Recognised Revenue
                </h3>
                <div>
                  <div className="t-flex t-justify-between t-items-center sm:t-gap-3 t-py-2">
                    <span className="t-text-body-sm t-text-text-60">
                      Revenue recognised from {summarySchedule?.period}
                      's invoices
                    </span>
                    <span className="t-text-body-sm t-text-text-60">
                      <AmountSuperScript
                        amount={Number(
                          summarySchedule?.recognized_revenue
                            ?.recognized_from_this_month || 0
                        )}
                      />
                    </span>
                  </div>
                  <div className="t-flex t-justify-between t-items-center sm:t-gap-3 t-py-2">
                    <span className="t-text-body-sm t-text-text-60">
                      Revenue previously deferred and recognised in{" "}
                      {summarySchedule?.period}
                    </span>
                    <span className="t-text-body-sm t-text-text-60">
                      <AmountSuperScript
                        amount={Number(
                          summarySchedule?.recognized_revenue
                            ?.recognized_from_previous_months || 0
                        )}
                      />
                    </span>
                  </div>
                  <div className="t-flex t-justify-between t-items-center sm:t-gap-3 t-py-2 t-border-t t-border-neutral-10 t-text-body-sm t-text-text-60 t-bg-neutral-0 t-px-3 t-rounded">
                    <span>
                      Total revenue recognised in {summarySchedule?.period}
                    </span>
                    <span>
                      <AmountSuperScript
                        amount={Number(
                          summarySchedule?.recognized_revenue
                            ?.total_recognized || 0
                        )}
                      />
                    </span>
                  </div>
                </div>
              </div>

              {/* Deferred Revenue */}
              <div>
                <h3 className="t-text-subtext t-text-text-100 t-mb-3">
                  Deferred Revenue
                </h3>
                <div>
                  <div className="t-flex t-justify-between t-items-center sm:t-gap-3 t-py-2">
                    <span className="t-text-body-sm t-text-text-60">
                      Revenue deferred as of previous month
                    </span>
                    <span className="t-text-body-sm t-text-text-60">
                      <AmountSuperScript
                        amount={Number(
                          summarySchedule?.deferred_revenue
                            ?.deferred_from_previous_months || 0
                        )}
                      />
                    </span>
                  </div>
                  <div className="t-flex t-justify-between t-items-center sm:t-gap-3 t-py-2">
                    <span className="t-text-body-sm t-text-text-60">
                      Revenue deferred from {summarySchedule?.period}
                      's invoices
                    </span>
                    <span className="t-text-body-sm t-text-text-60">
                      <AmountSuperScript
                        amount={Number(
                          summarySchedule?.deferred_revenue
                            ?.deferred_from_this_month || 0
                        )}
                      />
                    </span>
                  </div>
                  <div className="t-flex t-justify-between t-items-center sm:t-gap-3 t-py-2">
                    <span className="t-text-body-sm t-text-text-60">
                      Less revenue previously deferred and recognised in{" "}
                      {summarySchedule?.period}
                    </span>
                    <span className="t-text-body-sm t-text-text-60">
                      <AmountSuperScript
                        amount={Number(
                          summarySchedule?.deferred_revenue
                            ?.prev_deferred_and_recognized_in_current_month || 0
                        )}
                      />
                    </span>
                  </div>
                  <div className="t-flex t-justify-between t-items-center sm:t-gap-3 t-py-2 t-border-t t-border-neutral-10 t-text-body-sm t-text-text-60 t-bg-neutral-0 t-px-3 t-rounded">
                    <span>
                      Total revenue deferred as of {summarySchedule?.period}
                    </span>
                    <span>
                      <AmountSuperScript
                        amount={Number(
                          summarySchedule?.deferred_revenue?.total_deferred || 0
                        )}
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card.Root>
      )}
    </div>
  );
};

export const RevenueScheduleHome = () => {
  usePageTitle("Revenue Recognition");
  const entityId = useCurrentEntityId();
  const { path } = useRouteMatch();
  const { link } = useConstructInternalLink();
  const { data: bookkeepingSettings, isLoading: isLoadingBookkeepingSettings } =
    useGetBookkeepingSettingsQuery({
      entityId: entityId,
    });

  if (isLoadingBookkeepingSettings) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  if (!bookkeepingSettings?.deferred_revenue_enabled) {
    return <RevenueRecognitionLocked />;
  }

  return (
    <DashboardLayout
      header={
        <Header
          v2
          title="Revenue Recognition"
          bottom={
            <Tab.Root value="MATCH">
              <Tab.List className="!t-mb-6 sm:!t-gap-5">
                <Tab.NavTrigger exact value={link("", { includeUrl: true })}>
                  Overview
                </Tab.NavTrigger>
                <Tab.NavTrigger
                  exact
                  value={link("/waterfall", { includeUrl: true })}
                >
                  Revenue Waterfall
                </Tab.NavTrigger>
                <Tab.NavTrigger
                  exact
                  value={link("/customers", { includeUrl: true })}
                >
                  By Customer
                </Tab.NavTrigger>
              </Tab.List>
            </Tab.Root>
          }
        />
      }
    >
      <Switch>
        <Route exact path={path}>
          <RevenueScheduleOverview />
        </Route>
        <Route exact path={`${path}/waterfall`}>
          <RevenueWaterfall />
        </Route>
        <Route exact path={`${path}/customers`}>
          <RecognitionPartyWaterfall kind="REVENUE" />
        </Route>
      </Switch>
    </DashboardLayout>
  );
};
